import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import {
  MONTAGE_DETAILGRADE,
  MONTAGE_PUNKTE_MAX,
  MONTAGE_PUNKT_ARTEN,
  MONTAGE_TABELLEN_PFLICHT,
  MONTAGE_UMFAENGE,
  MONTAGE_VORLAGEN_MAX,
  montageExtra,
  type MontageExtra,
  type MontageVorlage,
  type MontageVorlagePunkt,
} from '@symphenia/shared-types'
import { ZiehBereich, useZeileGreifen, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeVor } from '@/components/ziehen/ziehen'
import { feldKlasse } from '@/lib/speichern'
import { cn } from '@/lib/utils'
import type { ErpEinstellungenProps } from '../registry'

const knopf = 'inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs'

/** Ein Text, der beim Verlassen die Liste als Ganzes schreibt; Esc stellt zurück. */
function Text({ wert, onSpeichern, platzhalter, className }: { wert: string | null; onSpeichern: (w: string | null) => void; platzhalter?: string; className?: string }) {
  const [lokal, setLokal] = useState(wert ?? '')
  useEffect(() => setLokal(wert ?? ''), [wert])
  return (
    <input
      className={cn(feldKlasse, className)}
      value={lokal}
      placeholder={platzhalter}
      aria-label={platzhalter}
      onChange={(e) => setLokal(e.target.value)}
      onBlur={() => (lokal.trim() || null) !== (wert || null) && onSpeichern(lokal.trim() || null)}
      onKeyDown={(e) => e.key === 'Escape' && setLokal(wert ?? '')}
    />
  )
}

const punktSchluessel = (vorhanden: readonly MontageVorlagePunkt[]) => {
  let i = vorhanden.length + 1
  while (vorhanden.some((p) => p.key === `p${i}`)) i++
  return `p${i}`
}

/**
 * AP-47 5.1, 11.2 — **Einstellungen des Montageprotokolls**: Vorgabe-Umfang, Detailgrad ohne Ausführungsklasse,
 * Bestätigungstext der Montage (der der Abnahme ist der allgemeine Bestätigungstext darüber) und die
 * Prüflisten-Vorlagen. Gespeichert wird die Liste als Ganzes über die Speicherstelle der Einstellungen
 * (AP-36 8.3 a); der Server prüft Aufbau und Grenzen (RN-2), vergibt Kennungen und zählt die Fassung einer
 * geänderten Vorlage hoch — Entwürfe behalten ihren Schnappschuss (MO-6). Ohne gespeicherte Vorlagen stehen
 * die drei Startvorlagen hier (MO-17).
 */
export function MontageEinstellungen({ extra, speichere }: ErpEinstellungenProps) {
  const { t } = useTranslation()
  const [stand, setStand] = useState<MontageExtra>(() => montageExtra(extra))
  const [offen, setOffen] = useState<string | null>(null)
  const behaelter = useRef<HTMLDivElement>(null)
  useEffect(() => setStand(montageExtra(extra)), [extra])

  const schreibe = (neu: MontageExtra) => {
    setStand(neu)
    void speichere(neu as unknown as Record<string, unknown>)
  }
  const vorlage = (id: string, teil: Partial<MontageVorlage>) => schreibe({ ...stand, vorlagen: stand.vorlagen.map((v) => (v.id === id ? { ...v, ...teil } : v)) })
  const sortiert = [...stand.vorlagen].sort((a, b) => a.sortOrder - b.sortOrder)

  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      const reihe = verschiebeVor(sortiert.map((v) => v.id), key, vorKey)
      schreibe({ ...stand, vorlagen: stand.vorlagen.map((v) => ({ ...v, sortOrder: reihe.indexOf(v.id) })) })
    },
  })

  return (
    <div className="space-y-3 border-t pt-3 text-sm">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-xs text-muted-foreground">
          {t('siteDocs.assembly.settings.defaultScope')}
          <select className={feldKlasse} value={stand.umfangVorgabe} onChange={(e) => schreibe({ ...stand, umfangVorgabe: e.target.value as MontageExtra['umfangVorgabe'] })}>
            {MONTAGE_UMFAENGE.map((u) => (
              <option key={u} value={u}>
                {t(`siteDocs.assembly.scopes.${u}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          {t('siteDocs.assembly.settings.defaultLevel')}
          <select className={feldKlasse} value={stand.detailgradVorgabe} onChange={(e) => schreibe({ ...stand, detailgradVorgabe: e.target.value as MontageExtra['detailgradVorgabe'] })}>
            {MONTAGE_DETAILGRADE.map((d) => (
              <option key={d} value={d}>
                {t(`siteDocs.assembly.levels.${d}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-xs text-muted-foreground">
        {t('siteDocs.assembly.settings.textAssembly')}
        <Text wert={stand.textMontage} onSpeichern={(w) => schreibe({ ...stand, textMontage: w })} />
        <span className="mt-1 block">{t('siteDocs.assembly.settings.textAcceptanceHint')}</span>
      </label>

      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('siteDocs.assembly.settings.templates')} ({stand.vorlagen.length}/{MONTAGE_VORLAGEN_MAX})
        </p>
        <button
          type="button"
          className={knopf}
          disabled={stand.vorlagen.length >= MONTAGE_VORLAGEN_MAX}
          onClick={() => {
            const id = crypto.randomUUID()
            schreibe({ ...stand, vorlagen: [...stand.vorlagen, { id, name: t('siteDocs.assembly.settings.newTemplate'), detailgrad: 'EINFACH', fassung: 1, aktiv: true, sortOrder: stand.vorlagen.length, tabellen: { anziehprotokoll: 'OPTIONAL', vermessung: 'OPTIONAL' }, punkte: [] }] })
            setOffen(id)
          }}
        >
          <Plus className="h-3.5 w-3.5" /> {t('siteDocs.assembly.settings.addTemplate')}
        </button>
      </div>
      <ZiehBereich dndProps={dndProps} vorschau={gezogen ? stand.vorlagen.find((v) => v.id === gezogen)?.name : null}>
        <div ref={behaelter} className="space-y-1">
          {sortiert.map((v) => (
            <VorlageZeile key={v.id} vorlage={v} offen={offen === v.id} onOffen={(o) => setOffen(o ? v.id : null)} ziellinie={ziel?.key === v.id ? ziel.kante : null} onChange={(teil) => vorlage(v.id, teil)} onEntfernen={() => schreibe({ ...stand, vorlagen: stand.vorlagen.filter((x) => x.id !== v.id) })} />
          ))}
        </div>
      </ZiehBereich>
    </div>
  )
}

function VorlageZeile({ vorlage: v, offen, onOffen, ziellinie, onChange, onEntfernen }: { vorlage: MontageVorlage; offen: boolean; onOffen: (o: boolean) => void; ziellinie: 'oben' | 'unten' | null; onChange: (teil: Partial<MontageVorlage>) => void; onEntfernen: () => void }) {
  const { t } = useTranslation()
  const greifen = useZeileGreifen(v.id)
  const punkt = (key: string, teil: Partial<MontageVorlagePunkt>) => onChange({ punkte: v.punkte.map((p) => (p.key === key ? { ...p, ...teil } : p)) })
  const schiebe = (i: number, um: number) => {
    const p = [...v.punkte]
    const [x] = p.splice(i, 1)
    p.splice(i + um, 0, x)
    onChange({ punkte: p })
  }
  return (
    <div data-key={v.id} ref={greifen.ref} {...(greifen.listeners ?? {})} className={cn('zeile-greifbar rounded-md border', ziellinie === 'oben' && 'border-t-2 border-t-primary', ziellinie === 'unten' && 'border-b-2 border-b-primary', greifen.isDragging && 'opacity-50')}>
      <div className="flex flex-wrap items-center gap-2 px-2 py-1">
        <button type="button" onClick={() => onOffen(!offen)} aria-label={t('siteDocs.assembly.settings.toggle')}>
          {offen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <Text className="w-56" wert={v.name} platzhalter={t('siteDocs.assembly.settings.templateName')} onSpeichern={(w) => w && onChange({ name: w })} />
        <select className={cn(feldKlasse, 'w-auto')} aria-label={t('siteDocs.assembly.level')} value={v.detailgrad} onChange={(e) => onChange({ detailgrad: e.target.value as MontageVorlage['detailgrad'] })}>
          {MONTAGE_DETAILGRADE.map((d) => (
            <option key={d} value={d}>
              {t(`siteDocs.assembly.levels.${d}`)}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={v.aktiv} onChange={(e) => onChange({ aktiv: e.target.checked })} /> {t('siteDocs.assembly.settings.active')}
        </label>
        <span className="text-xs text-muted-foreground">
          {t('siteDocs.assembly.version')} {v.fassung} · {v.punkte.length} {t('siteDocs.assembly.settings.points')}
        </span>
        <button type="button" className={cn(knopf, 'ml-auto text-red-700')} onClick={onEntfernen} title={t('siteDocs.assembly.settings.removeTemplate')}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {offen && (
        <div className="space-y-2 border-t px-2 py-2" data-nicht-greifen>
          {v.detailgrad === 'NACHWEIS' && (
            <div className="flex flex-wrap gap-3 text-xs">
              {(['anziehprotokoll', 'vermessung'] as const).map((tab) => (
                <label key={tab} className="flex items-center gap-1">
                  {t(`siteDocs.assembly.settings.table.${tab}`)}
                  <select className={cn(feldKlasse, 'w-auto')} value={v.tabellen[tab]} onChange={(e) => onChange({ tabellen: { ...v.tabellen, [tab]: e.target.value } })}>
                    {MONTAGE_TABELLEN_PFLICHT.map((p) => (
                      <option key={p} value={p}>
                        {t(`siteDocs.assembly.settings.tableRule.${p}`)}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          )}
          {v.punkte.map((p, i) => (
            <div key={p.key} className="grid items-center gap-1 sm:grid-cols-[8rem_1fr_7rem_4rem_4rem_4rem_4rem_auto_auto_auto]">
              <Text wert={p.gruppe} platzhalter={t('siteDocs.assembly.group')} onSpeichern={(w) => punkt(p.key, { gruppe: w })} />
              <Text wert={p.text} platzhalter={t('siteDocs.assembly.point')} onSpeichern={(w) => w && punkt(p.key, { text: w })} />
              <select className={feldKlasse} aria-label={t('siteDocs.assembly.settings.kind')} value={p.art} onChange={(e) => punkt(p.key, { art: e.target.value as MontageVorlagePunkt['art'] })}>
                {MONTAGE_PUNKT_ARTEN.map((a) => (
                  <option key={a} value={a}>
                    {t(`siteDocs.assembly.settings.kinds.${a}`)}
                  </option>
                ))}
              </select>
              {p.art === 'MESSEN' ? (
                <>
                  <Text wert={p.einheit} platzhalter={t('siteDocs.assembly.unit')} onSpeichern={(w) => punkt(p.key, { einheit: w })} />
                  <Text wert={p.soll} platzhalter={t('siteDocs.assembly.target')} onSpeichern={(w) => punkt(p.key, { soll: w })} />
                  <Text wert={p.tolMinus} platzhalter="−" onSpeichern={(w) => punkt(p.key, { tolMinus: w })} />
                  <Text wert={p.tolPlus} platzhalter="+" onSpeichern={(w) => punkt(p.key, { tolPlus: w })} />
                </>
              ) : (
                <span className="sm:col-span-4">
                  {p.art === 'TEXT' && (
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={p.pflicht} onChange={(e) => punkt(p.key, { pflicht: e.target.checked })} /> {t('siteDocs.assembly.required')}
                    </label>
                  )}
                </span>
              )}
              <label className="flex items-center gap-1 text-xs" title={t('siteDocs.assembly.settings.photoHint')}>
                <input type="checkbox" checked={p.fotoPflicht} onChange={(e) => punkt(p.key, { fotoPflicht: e.target.checked })} /> {t('siteDocs.assembly.settings.photo')}
              </label>
              <span className="flex">
                <button type="button" className="px-1 disabled:opacity-30" disabled={i === 0} onClick={() => schiebe(i, -1)} aria-label={t('siteDocs.assembly.settings.up')}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" className="px-1 disabled:opacity-30" disabled={i === v.punkte.length - 1} onClick={() => schiebe(i, 1)} aria-label={t('siteDocs.assembly.settings.down')}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
              </span>
              <button type="button" className="text-red-700" onClick={() => onChange({ punkte: v.punkte.filter((x) => x.key !== p.key) })} aria-label={t('siteDocs.assembly.settings.removePoint')}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className={knopf}
            disabled={v.punkte.length >= MONTAGE_PUNKTE_MAX}
            onClick={() => onChange({ punkte: [...v.punkte, { key: punktSchluessel(v.punkte), gruppe: v.punkte[v.punkte.length - 1]?.gruppe ?? null, text: t('siteDocs.assembly.settings.newPoint'), art: 'PRUEFEN', einheit: null, soll: null, tolMinus: null, tolPlus: null, fotoPflicht: false, pflicht: false }] })}
          >
            <Plus className="h-3.5 w-3.5" /> {t('siteDocs.assembly.settings.addPoint')} ({v.punkte.length}/{MONTAGE_PUNKTE_MAX})
          </button>
        </div>
      )}
    </div>
  )
}
